import type { CenaTabletop } from '../../../dominio/tabletop'
import type { CamadasFonte } from '../../../dominio/tabletop/camadas'
import type { VolumeEnquadramento } from '../../../visualizacao3d/camera/comandosCamera'
import { sentidoAFavorDoVento } from '../../../dominio/vento'
import { meteorologiaDaCena } from '../../../dominio/tabletop'

export function volumeDaMesa(
  cena: CenaTabletop,
  camadas: readonly CamadasFonte[],
  ancora?: Pick<VolumeEnquadramento, 'centro' | 'alturaAlvoM'>,
): VolumeEnquadramento {
  const pontos = cena.objetos
    .filter((o) => !o.oculto)
    .map(
      (o) => [o.transformacao.posicao[0], o.transformacao.posicao[2]] as const,
    )
  const [dx, dz] = sentidoAFavorDoVento(
    meteorologiaDaCena(cena).direcaoVentoGraus,
  )
  for (const c of camadas) {
    const raio = Math.max(c.isolamentoGreM, c.bleve?.zonas.raioMornaM ?? 0)
    pontos.push(
      [c.origem[0] - raio, c.origem[1] - raio],
      [c.origem[0] + raio, c.origem[1] + raio],
    )
    const alcance = Math.max(
      c.acaoProtetora?.protegerM ?? 0,
      ...(c.pluma?.contornos.map((p) => p.alcanceM) ?? []),
    )
    const largura = Math.max(
      (c.acaoProtetora?.protegerM ?? 0) / 2,
      ...(c.pluma?.contornos.flatMap((p) => p.borda.map(([, w]) => w)) ?? []),
    )
    for (const distancia of [0, alcance]) {
      for (const lateral of [-largura, largura]) {
        pontos.push([
          c.origem[0] + dx * distancia + dz * lateral,
          c.origem[1] + dz * distancia - dx * lateral,
        ])
      }
    }
  }
  if (!pontos.length) pontos.push([0, 0])
  const xs = pontos.map(([x]) => x),
    zs = pontos.map(([, z]) => z)
  const minX = Math.min(...xs),
    maxX = Math.max(...xs),
    minZ = Math.min(...zs),
    maxZ = Math.max(...zs)
  const visiveis = cena.objetos.filter((o) => !o.oculto)
  const referencia =
    visiveis.find((o) => o.tipo === 'caminhaoTanque') ??
    visiveis.find((o) => o.fonte)
  const centroX =
    ancora?.centro?.[0] ??
    referencia?.transformacao.posicao[0] ??
    (minX + maxX) / 2
  const centroZ =
    ancora?.centro?.[1] ??
    referencia?.transformacao.posicao[2] ??
    (minZ + maxZ) / 2

  // Enquadramento padrão: só os objetos de verdade, sem as camadas de risco
  // — uma zona de isolamento de centenas de metros não deve mandar a câmera
  // para longe de um punhado de bombeiros e viaturas a poucos metros.
  const pontosObjetos = visiveis.map(
    (o) => [o.transformacao.posicao[0], o.transformacao.posicao[2]] as const,
  )
  const raioObjetosM = pontosObjetos.reduce(
    (maior, [x, z]) => Math.max(maior, Math.hypot(x - centroX, z - centroZ)),
    0,
  )

  return {
    comprimento: Math.max(
      100,
      2 * Math.max(Math.abs(maxZ - centroZ), Math.abs(minZ - centroZ)) * 1.15,
    ),
    largura: Math.max(
      100,
      2 * Math.max(Math.abs(maxX - centroX), Math.abs(minX - centroX)) * 1.15,
    ),
    altura: 20,
    raioInicialM: Math.max(15, raioObjetosM * 1.3),
    ...(ancora
      ? { alturaAlvoM: ancora.alturaAlvoM }
      : referencia
        ? { alturaAlvoM: referencia.transformacao.posicao[1] + 1.5 }
        : {}),
    centro: [centroX, centroZ],
  }
}
